import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { TableColumn } from 'uiComponents/table/reactTable/interface'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { getFiltersFromUrl } from 'uiComponents/filter/filterHelpers'
import { InfiniteTable } from 'uiComponents/table/reactTable/infiniteTable'
import { useLazyGetDailyReportQuery } from 'orders/reduxQueries'
import { format } from 'date-fns'
import { EntityType, IDailyReportResult } from '../schemas'
import PrintViewHeader from './components/printView'
import { ApfFieldsResponse } from 'orders/ordersService'
import CustomerInfoDetails from './components/apfColumn'
import InfoColumn from './components/infoColumn'
import OpenRegular from './icons/openRegular.svg'
import { useTranslation } from 'translations'
import './styles.scss'

interface DailyScheduleTableProps {
    selectedDate: Date
    printRef: React.RefObject<HTMLDivElement>
    toolbar?: React.ReactNode
    apfFieldsKey: ApfFieldsResponse
}

const DailyScheduleTable: React.FC<DailyScheduleTableProps> = ({ selectedDate, printRef, apfFieldsKey, toolbar }) => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const location = useLocation()
    const history = useHistory()
    const [fetchData, { data }] = useLazyGetDailyReportQuery()
    const [allData, setAllData] = useState<IDailyReportResult[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [filtersInUse, setFiltersInUse] = useState<boolean>(false)
    const [locationName, setLocationName] = useState<string>('')

    useEffect(() => {
        const apfFields = getFiltersFromUrl('apf_fields').split(',').filter(Boolean)
        const products = getFiltersFromUrl('products').split(',').filter(Boolean)

        setFiltersInUse(apfFields.length > 0 || products.length > 0)
    }, [location.search])

    const fetchOrderData = useCallback(
        (url?: string) => {
            if (!accountSlug) return

            const products = getFiltersFromUrl('products').split(',').filter(Boolean)
            const apfFields = getFiltersFromUrl('apf_fields').split(',').filter(Boolean)
            const locationIds = getFiltersFromUrl('location_id')
            setIsLoading(true)

            return fetchData({
                accountSlug,
                date: format(selectedDate, 'yyyy-MM-dd'),
                products,
                apfFields,
                locationIds,
                url,
            })
                .unwrap()
                .then((response) => {
                    if (response && response.results) {
                        setAllData((prev) => {
                            if (url) {
                                return [...prev, ...response.results]
                            }
                            return response.results
                        })
                    }
                })
                .catch((error) => {
                    console.error('Error fetching order data:', error)
                })
                .finally(() => {
                    setIsLoading(false)
                })
        },
        [accountSlug, selectedDate, location.search],
    )

    useEffect(() => {
        if (!accountSlug) return
        setAllData([])
        fetchOrderData()
    }, [accountSlug, selectedDate, location.search])

    useEffect(() => {
        const locationIds = getFiltersFromUrl('location_id')
        if (data?.results && locationIds !== '' && data.results[0]) {
            setLocationName(data.results[0].location.name)
        }
    }, [data?.results, location.search])

    const loadMore = useCallback(() => {
        if (data?.next && accountSlug) {
            fetchOrderData(data.next)
        }
    }, [accountSlug, data?.next, location.search, selectedDate])

    const columns = useMemo<TableColumn<IDailyReportResult>[]>(
        () => [
            {
                Header: t('orders.dailySchedule.columns.details'),
                accessor: 'visitTime',
                width: '197px',
                style: { padding: '0.5rem' },
                Cell: ({ row: { original } }) => <InfoColumn dailyReportResult={original} />,
            },
            {
                Header: t('orders.dailySchedule.columns.buyerFields'),
                accessor: 'apfDetails',
                width: '395px',
                style: { padding: '0.5rem' },
                disableSortBy: true,
                Cell: ({ value }) => {
                    const apfFields = getFiltersFromUrl('apf_fields').split(',').filter(Boolean)
                    const shouldHideBuyerEmail =
                        !apfFields.includes('buyerEmail') && apfFields.length > 0 && value.buyerEmail
                    const shouldHideFieldsExceptBuyerEmail = apfFields.length === 1 && apfFields[0] === 'buyerEmail'

                    return (
                        <CustomerInfoDetails
                            value={value}
                            apfFieldsKey={apfFieldsKey}
                            shouldHideBuyerEmail={shouldHideBuyerEmail}
                            shouldHideFieldsExceptBuyerEmail={shouldHideFieldsExceptBuyerEmail}
                        />
                    )
                },
            },
            {
                Header: t('orders.dailySchedule.columns.products'),
                accessor: 'products',
                width: '276px',
                style: { padding: '0.5rem' },
                disableSortBy: true,
                Cell: ({ value }: { value: { [key: string]: { productName: string; quantity: number } } }) => (
                    <div className="products-container">
                        {Object.entries(value).map(([id, product]) => (
                            <div key={id} className="product-row">
                                <div className="product-name" title={product.productName}>
                                    <span className="product-quantity">
                                        {t('orders.dailySchedule.productQuantity', { quantity: product.quantity })}
                                    </span>
                                    &nbsp;
                                    <span className="product-name-text">{product.productName}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                Header: t('orders.dailySchedule.columns.notes'),
                accessor: 'comments',
                width: '276px',
                wrap: false,
                style: { padding: '0.5rem' },
                className: 'wrapContent',
                disableSortBy: true,
                Cell: ({ value }: { value: Array<{ uuid: string; content: string; isActive: boolean }> }) => {
                    const activeComment = value?.find((comment) => comment.isActive)
                    return <div className="comment__text">{activeComment?.content}</div>
                },
            },
            {
                id: 'order',
                accessor: 'order',
                width: '56px',
                style: { padding: '0.5rem', overflow: 'visible' },
                disableSortBy: true,
                Cell: ({ row: { original } }) => (
                    <div className="order-icon-cell">
                        <div
                            className="order-icon-container"
                            onClick={() => {
                                if (original.entityType === EntityType.ORDER) {
                                    history.push(
                                        `/account/${accountSlug}/orders/${original.entityUuid}/details?origin=daily-schedule`,
                                    )
                                } else {
                                    history.push(
                                        `/account/${accountSlug}/orders/pending-reservations?q=${original.entityId}`,
                                    )
                                }
                            }}
                        >
                            <OpenRegular className="open-icon" />
                        </div>
                    </div>
                ),
            },
        ],
        [apfFieldsKey, t],
    )

    return (
        <div className="daily-schedule-table-container" ref={printRef}>
            <PrintViewHeader selectedDate={selectedDate} locationName={locationName} />
            <InfiniteTable
                toolbar={toolbar}
                data={allData}
                columns={columns}
                loading={allData.length === 0 && isLoading}
                loadingMore={isLoading}
                pagination={{
                    next: data?.next,
                    previous: data?.previous,
                }}
                onLoadMore={loadMore}
                noResultsRow={{
                    text: filtersInUse
                        ? t('orders.dailySchedule.noResultsFiltered')
                        : t('orders.dailySchedule.noResults'),
                }}
                headerProps={{ className: 'daily-schedule-header-row' }}
                cellProps={() => ({
                    noPadding: true,
                    className: 'daily-schedule-cell',
                })}
                rowProps={() => ({
                    className: 'daily-schedule-row',
                })}
                tableProps={{
                    className: 'daily-schedule-table',
                }}
                knowledgeLink="https://support.convious.com/help/how-to-manage-orders"
                bordered
                storeStateInQuery={false}
            />
        </div>
    )
}

export default DailyScheduleTable
